import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Clock,
  User,
  Zap,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Search,
  AlertTriangle,
  Stethoscope,
  Video,
  FileText,
  Phone,
  ShieldAlert,
  ExternalLink,
  Activity,
  UserCheck,
} from 'lucide-react';
import { expressApi } from '@/api/express-api';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';

interface QueueStatus {
  waitingCount: number;
  inProgressCount: number;
  availableDoctors: number;
  averageWaitTime: number;
}

interface WaitingPatient {
  id: string;
  appointmentId?: string;
  patientName: string;
  patientEmail?: string;
  patientPhone?: string;
  patientAge: number | null;
  patientGender: string;
  specialty: string;
  priority: 'NORMAL' | 'URGENT' | 'EMERGENCY' | string;
  triageLevel: 'LOW' | 'MODERATE' | 'NORMAL' | 'HIGH' | 'EMERGENCY' | string;
  queuePosition: number;
  waitingSince: string;
  aiSummary?: string;
  status?: string;
}

interface DoctorOnline {
  id: string;
  name: string;
  specialty: string;
  status: string;
  crm?: string | null;
  email?: string | null;
  phone?: string | null;
  lastSeenAt?: string | null;
}

interface InProgressAppointment {
  id: string;
  expressId?: string;
  sessionId?: string;
  patientName: string;
  patientEmail?: string;
  patientAge?: number | null;
  doctorName: string;
  doctorCrm?: string | null;
  specialty: string;
  startedAt: string;
  duration: number;
  status?: string;
}

export function AdminQueuePage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('ALL');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTriagePatient, setSelectedTriagePatient] = useState<WaitingPatient | null>(null);
  const [patientToCancel, setPatientToCancel] = useState<WaitingPatient | null>(null);
  const [cancelReason, setCancelReason] = useState('Cancelado pela supervisão médica');

  // Query: Status geral da fila
  const { data: queueStatus, refetch: refetchStatus } = useQuery<QueueStatus>({
    queryKey: ['admin', 'queue', 'status'],
    queryFn: async () => {
      return expressApi.getQueueStatus();
    },
    refetchInterval: 5000,
  });

  // Query: Pacientes aguardando
  const { data: waitingPatientsData, refetch: refetchWaiting } = useQuery<{ data: WaitingPatient[] }>({
    queryKey: ['admin', 'queue', 'waiting'],
    queryFn: async () => {
      const res = await expressApi.getWaitingPatients();
      return { data: res?.data || [] };
    },
    refetchInterval: 5000,
  });

  // Query: Médicos online
  const { data: doctorsOnlineData, refetch: refetchDoctors } = useQuery<{
    count: number;
    specialties: { id: string; name: string; count: number }[];
    data?: DoctorOnline[];
  }>({
    queryKey: ['admin', 'queue', 'doctors-online'],
    queryFn: async () => {
      const res = await expressApi.getDoctorsOnline();
      return res;
    },
    refetchInterval: 10000,
  });

  // Query: Consultas em andamento
  const { data: inProgressData, refetch: refetchInProgress } = useQuery<{ data: InProgressAppointment[] }>({
    queryKey: ['admin', 'queue', 'in-progress'],
    queryFn: async () => {
      const res = await expressApi.getInProgressAppointments();
      return { data: res?.data || [] };
    },
    refetchInterval: 5000,
  });

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchStatus(),
        refetchWaiting(),
        refetchDoctors(),
        refetchInProgress(),
      ]);
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const cancelExpressMutation = useMutation({
    mutationFn: async ({ expressId, reason }: { expressId: string; reason: string }) => {
      return expressApi.cancelExpress(expressId, reason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'queue'] });
      setPatientToCancel(null);
    },
  });

  const waitingPatients = useMemo(() => {
    const list = waitingPatientsData?.data || [];
    return list.filter((p) => {
      const matchesSearch =
        searchQuery === '' ||
        p.patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.patientEmail && p.patientEmail.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (p.aiSummary && p.aiSummary.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesSpecialty =
        selectedSpecialty === 'ALL' || p.specialty.toLowerCase() === selectedSpecialty.toLowerCase();
      return matchesSearch && matchesSpecialty;
    });
  }, [waitingPatientsData, searchQuery, selectedSpecialty]);

  const doctorsOnline = useMemo(() => {
    const list = doctorsOnlineData?.data || [];
    return list.filter((d) => {
      const matchesSearch =
        searchQuery === '' ||
        d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (d.crm && d.crm.toLowerCase().includes(searchQuery.toLowerCase())) ||
        d.specialty.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSpecialty =
        selectedSpecialty === 'ALL' || d.specialty.toLowerCase() === selectedSpecialty.toLowerCase();
      return matchesSearch && matchesSpecialty;
    });
  }, [doctorsOnlineData, searchQuery, selectedSpecialty]);

  const inProgressAppointments = useMemo(() => {
    const list = inProgressData?.data || [];
    return list.filter((a) => {
      const matchesSearch =
        searchQuery === '' ||
        a.patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.doctorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.specialty.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSpecialty =
        selectedSpecialty === 'ALL' || a.specialty.toLowerCase() === selectedSpecialty.toLowerCase();
      return matchesSearch && matchesSpecialty;
    });
  }, [inProgressData, searchQuery, selectedSpecialty]);

  const availableSpecialties = useMemo(() => {
    const specs = new Set<string>();
    waitingPatientsData?.data?.forEach((p) => p.specialty && specs.add(p.specialty));
    doctorsOnlineData?.data?.forEach((d) => d.specialty && specs.add(d.specialty));
    doctorsOnlineData?.specialties?.forEach((s) => s.name && specs.add(s.name));
    inProgressData?.data?.forEach((i) => i.specialty && specs.add(i.specialty));
    return Array.from(specs);
  }, [waitingPatientsData, doctorsOnlineData, inProgressData]);

  const getPriorityBadge = (priority?: string, triageLevel?: string) => {
    const level = (triageLevel || priority || 'NORMAL').toUpperCase();
    if (level === 'EMERGENCY') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-400 border border-red-200 dark:border-red-800 animate-pulse">
          <ShieldAlert className="h-3 w-3" />
          Emergência (Vermelho)
        </span>
      );
    }
    if (level === 'HIGH' || level === 'URGENT') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-700 dark:bg-orange-950/60 dark:text-orange-400 border border-orange-200 dark:border-orange-800">
          <AlertTriangle className="h-3 w-3" />
          Muito Urgente (Laranja)
        </span>
      );
    }
    if (level === 'MODERATE') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
          <Clock className="h-3 w-3" />
          Urgente (Amarelo)
        </span>
      );
    }
    if (level === 'LOW') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-400 border border-green-200 dark:border-green-800">
          <CheckCircle2 className="h-3 w-3" />
          Pouco Urgente (Verde)
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
        Normal (Azul)
      </span>
    );
  };

  const formatWaitTime = (dateStr: string) => {
    const diffMinutes = Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60)));
    if (diffMinutes < 1) return 'Agora mesmo';
    if (diffMinutes < 60) return `${diffMinutes} min`;
    const hours = Math.floor(diffMinutes / 60);
    const mins = diffMinutes % 60;
    return `${hours}h ${mins}min`;
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-7xl mx-auto">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
              Filas de Atendimento
            </h1>
            <span className="flex h-2.5 w-2.5 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Painel operacional em tempo real de triagem clínica, plantonistas e consultas expressas
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800/80 px-3 py-1.5 rounded-full flex items-center gap-2 border border-neutral-200 dark:border-neutral-700">
            <Clock className="h-3.5 w-3.5 text-neutral-400" />
            Atualizado: {new Date().toLocaleTimeString('pt-BR')}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-1.5 shadow-sm"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin text-primary' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card: Aguardando */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200/80 dark:border-neutral-800 p-5 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                Aguardando Fila
              </p>
              <p className="text-3xl font-extrabold text-orange-600 dark:text-orange-500 mt-1">
                {queueStatus?.waitingCount ?? waitingPatients.length}
              </p>
              <p className="text-xs text-neutral-400 mt-0.5">Pacientes prontos para plantão</p>
            </div>
            <div className="p-3 bg-orange-50 dark:bg-orange-950/40 border border-orange-100 dark:border-orange-900/50 rounded-2xl text-orange-600 dark:text-orange-400">
              <Users className="h-6 w-6" />
            </div>
          </div>
        </div>

        {/* Card: Em Consulta */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200/80 dark:border-neutral-800 p-5 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                Em Consulta
              </p>
              <p className="text-3xl font-extrabold text-blue-600 dark:text-blue-500 mt-1">
                {queueStatus?.inProgressCount ?? inProgressAppointments.length}
              </p>
              <p className="text-xs text-neutral-400 mt-0.5">Teleconsultas ativas agora</p>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900/50 rounded-2xl text-blue-600 dark:text-blue-400">
              <Zap className="h-6 w-6" />
            </div>
          </div>
        </div>

        {/* Card: Médicos Online */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200/80 dark:border-neutral-800 p-5 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                Plantonistas Online
              </p>
              <p className="text-3xl font-extrabold text-emerald-600 dark:text-emerald-500 mt-1">
                {queueStatus?.availableDoctors ?? (doctorsOnlineData?.count ?? doctorsOnline.length)}
              </p>
              <p className="text-xs text-neutral-400 mt-0.5">Médicos prontos para atender</p>
            </div>
            <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-900/50 rounded-2xl text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-6 w-6" />
            </div>
          </div>
        </div>

        {/* Card: Tempo Médio de Espera */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200/80 dark:border-neutral-800 p-5 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                Tempo Médio Fila
              </p>
              <p className="text-3xl font-extrabold text-purple-600 dark:text-purple-500 mt-1">
                {queueStatus?.averageWaitTime ?? 0} <span className="text-lg font-bold">min</span>
              </p>
              <p className="text-xs text-neutral-400 mt-0.5">SLA de plantão estimado</p>
            </div>
            <div className="p-3 bg-purple-50 dark:bg-purple-950/40 border border-purple-100 dark:border-purple-900/50 rounded-2xl text-purple-600 dark:text-purple-400">
              <Clock className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Buscar por paciente, médico, CRM, queixa clínica ou e-mail..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        {availableSpecialties.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
            <span className="text-xs text-neutral-400 font-medium whitespace-nowrap">Especialidade:</span>
            <select
              value={selectedSpecialty}
              onChange={(e) => setSelectedSpecialty(e.target.value)}
              className="px-3 py-2 text-sm bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 text-neutral-800 dark:text-neutral-200"
            >
              <option value="ALL">Todas as especialidades</option>
              {availableSpecialties.map((spec) => (
                <option key={spec} value={spec}>
                  {spec}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Main Grid: Pacientes Aguardando & Médicos Online */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section: Pacientes Aguardando */}
        <div className="rounded-3xl bg-white dark:bg-neutral-900 border border-neutral-200/80 dark:border-neutral-800 p-6 shadow-sm flex flex-col h-full">
          <div className="flex items-center justify-between pb-4 border-b border-neutral-100 dark:border-neutral-800">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-orange-100/70 dark:bg-orange-950/50 rounded-xl text-orange-600 dark:text-orange-400">
                <Users className="h-5 w-5" />
              </div>
              <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
                Pacientes Aguardando ({waitingPatients.length})
              </h2>
            </div>
            {waitingPatients.length > 0 && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 dark:bg-orange-950/60 dark:text-orange-400 border border-orange-200 dark:border-orange-800">
                Fila Ativa
              </span>
            )}
          </div>

          <div className="pt-4 flex-1">
            {waitingPatients.length === 0 ? (
              <div className="py-12 text-center flex flex-col items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-neutral-50 dark:bg-neutral-800 flex items-center justify-center text-neutral-300 dark:text-neutral-600 mb-3">
                  <UserCheck className="h-7 w-7" />
                </div>
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Nenhum paciente aguardando atendimento
                </p>
                <p className="text-xs text-neutral-400 mt-1">
                  Assim que novos pacientes iniciarem a triagem expressa, eles aparecerão aqui.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {waitingPatients.map((patient) => (
                  <div
                    key={patient.id}
                    className="p-4 bg-neutral-50/70 dark:bg-neutral-800/40 rounded-2xl border border-neutral-200/70 dark:border-neutral-700/60 hover:border-orange-300 dark:hover:border-orange-800 transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col items-center justify-center w-9 h-9 rounded-xl bg-orange-100 dark:bg-orange-950/70 text-orange-700 dark:text-orange-400 font-black text-sm shrink-0 border border-orange-200 dark:border-orange-800">
                          #{patient.queuePosition}
                        </div>

                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
                              {patient.patientName}
                            </h3>
                            {getPriorityBadge(patient.priority, patient.triageLevel)}
                          </div>

                          <div className="flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                            {patient.patientAge && <span>{patient.patientAge} anos</span>}
                            <span>•</span>
                            <span className="font-medium text-neutral-700 dark:text-neutral-300">
                              {patient.specialty}
                            </span>
                            {patient.patientPhone && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {patient.patientPhone}
                                </span>
                              </>
                            )}
                          </div>

                          {patient.aiSummary && (
                            <div className="mt-2 text-xs bg-white dark:bg-neutral-900/80 p-2.5 rounded-xl border border-neutral-200/80 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 line-clamp-2">
                              <span className="font-semibold text-neutral-800 dark:text-neutral-200">
                                Triagem IA:{' '}
                              </span>
                              {patient.aiSummary}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <div className="text-right">
                          <span className="text-xs font-semibold text-orange-600 dark:text-orange-400">
                            {formatWaitTime(patient.waitingSince)}
                          </span>
                          <p className="text-[10px] text-neutral-400">em espera</p>
                        </div>

                        <div className="flex items-center gap-1 mt-1">
                          {patient.aiSummary && (
                            <button
                              onClick={() => setSelectedTriagePatient(patient)}
                              className="p-1.5 text-neutral-600 hover:text-primary dark:text-neutral-400 dark:hover:text-primary-foreground hover:bg-neutral-200/60 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                              title="Ver resumo clínico e triagem"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                          )}

                          <button
                            onClick={() => setPatientToCancel(patient)}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors"
                            title="Cancelar atendimento"
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Section: Médicos Online */}
        <div className="rounded-3xl bg-white dark:bg-neutral-900 border border-neutral-200/80 dark:border-neutral-800 p-6 shadow-sm flex flex-col h-full">
          <div className="flex items-center justify-between pb-4 border-b border-neutral-100 dark:border-neutral-800">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-emerald-100/70 dark:bg-emerald-950/50 rounded-xl text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
                Médicos Online ({doctorsOnline.length || doctorsOnlineData?.count || 0})
              </h2>
            </div>
            {(doctorsOnline.length > 0 || (doctorsOnlineData?.count ?? 0) > 0) && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                Disponíveis
              </span>
            )}
          </div>

          <div className="pt-4 flex-1">
            {doctorsOnline.length === 0 ? (
              <div className="py-12 text-center flex flex-col items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-neutral-50 dark:bg-neutral-800 flex items-center justify-center text-neutral-300 dark:text-neutral-600 mb-3">
                  <Stethoscope className="h-7 w-7" />
                </div>
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Nenhum médico plantonista online no momento
                </p>
                <p className="text-xs text-neutral-400 mt-1">
                  Os médicos que ativarem o plantão express aparecerão automaticamente nesta lista.
                </p>

                {doctorsOnlineData?.specialties && doctorsOnlineData.specialties.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2 justify-center">
                    {doctorsOnlineData.specialties.map((spec) => (
                      <span
                        key={spec.id}
                        className="text-xs px-2.5 py-1 bg-neutral-100 dark:bg-neutral-800 rounded-lg text-neutral-600 dark:text-neutral-300"
                      >
                        {spec.name}: {spec.count}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {doctorsOnline.map((doctor) => (
                  <div
                    key={doctor.id}
                    className="p-4 bg-neutral-50/70 dark:bg-neutral-800/40 rounded-2xl border border-neutral-200/70 dark:border-neutral-700/60 hover:border-emerald-300 dark:hover:border-emerald-800 transition-all flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-950/60 flex items-center justify-center text-emerald-700 dark:text-emerald-400 font-bold border border-emerald-200 dark:border-emerald-800">
                        <User className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
                            {doctor.name}
                          </h3>
                          {doctor.crm && (
                            <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400">
                              CRM {doctor.crm}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                          {doctor.specialty}
                        </p>
                        {doctor.phone && (
                          <p className="text-[11px] text-neutral-400 flex items-center gap-1 mt-0.5">
                            <Phone className="h-3 w-3" />
                            {doctor.phone}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                        Disponível
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section: Em Consulta */}
      <div className="rounded-3xl bg-white dark:bg-neutral-900 border border-neutral-200/80 dark:border-neutral-800 p-6 shadow-sm">
        <div className="flex items-center justify-between pb-4 border-b border-neutral-100 dark:border-neutral-800">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-blue-100/70 dark:bg-blue-950/50 rounded-xl text-blue-600 dark:text-blue-400">
              <Zap className="h-5 w-5" />
            </div>
            <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
              Consultas em Andamento ({inProgressAppointments.length})
            </h2>
          </div>
          {inProgressAppointments.length > 0 && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
              Salas Ativas
            </span>
          )}
        </div>

        <div className="pt-4">
          {inProgressAppointments.length === 0 ? (
            <div className="py-10 text-center flex flex-col items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-neutral-50 dark:bg-neutral-800 flex items-center justify-center text-neutral-300 dark:text-neutral-600 mb-3">
                <Video className="h-7 w-7" />
              </div>
              <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Nenhuma teleconsulta expressa em andamento neste momento
              </p>
              <p className="text-xs text-neutral-400 mt-1">
                Quando um médico aceitar uma chamada da fila, a sessão aparecerá aqui.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {inProgressAppointments.map((apt) => (
                <div
                  key={apt.id}
                  className="p-4 bg-blue-50/50 dark:bg-blue-950/20 rounded-2xl border border-blue-200/70 dark:border-blue-800/60 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 dark:text-blue-400">
                        <Video className="h-4 w-4" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
                          {apt.patientName}
                        </h4>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          Dr(a). {apt.doctorName}
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      <span className="text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-100/70 dark:bg-blue-900/60 px-2 py-0.5 rounded-full">
                        {apt.duration} min
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-blue-100 dark:border-blue-900/40 flex items-center justify-between text-xs">
                    <span className="text-neutral-500 dark:text-neutral-400 font-medium">
                      {apt.specialty}
                    </span>
                    {apt.sessionId && (
                      <a
                        href={`/telemedicina/${apt.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                      >
                        Supervisionar
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal: Detalhes da Triagem IA */}
      <Dialog open={!!selectedTriagePatient} onOpenChange={(open) => !open && setSelectedTriagePatient(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Triagem Clínica do Paciente
            </DialogTitle>
          </DialogHeader>

          {selectedTriagePatient && (
            <div className="space-y-4 pt-2">
              <div className="p-3 bg-neutral-50 dark:bg-neutral-800 rounded-xl space-y-1">
                <p className="font-semibold text-neutral-900 dark:text-neutral-100">
                  {selectedTriagePatient.patientName}
                </p>
                <div className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-3">
                  {selectedTriagePatient.patientAge && (
                    <span>Idade: {selectedTriagePatient.patientAge} anos</span>
                  )}
                  <span>Especialidade: {selectedTriagePatient.specialty}</span>
                  <span>Posição: #{selectedTriagePatient.queuePosition}</span>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                  Classificação de Risco
                </p>
                <div>{getPriorityBadge(selectedTriagePatient.priority, selectedTriagePatient.triageLevel)}</div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                  Resumo Clínico da IA & Queixa Principal
                </p>
                <div className="p-3 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed whitespace-pre-wrap">
                  {selectedTriagePatient.aiSummary || 'Nenhum resumo clínico registrado'}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => setSelectedTriagePatient(null)}>
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Confirmar Cancelamento da Fila */}
      <Dialog open={!!patientToCancel} onOpenChange={(open) => !open && setPatientToCancel(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Cancelar Atendimento
            </DialogTitle>
          </DialogHeader>

          {patientToCancel && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-neutral-600 dark:text-neutral-300">
                Tem certeza de que deseja remover o paciente{' '}
                <strong className="text-neutral-900 dark:text-neutral-100">{patientToCancel.patientName}</strong> da
                fila de espera?
              </p>

              <div>
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                  Motivo do cancelamento:
                </label>
                <input
                  type="text"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setPatientToCancel(null)}
                  disabled={cancelExpressMutation.isPending}
                >
                  Voltar
                </Button>
                <Button
                  variant="primary"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() =>
                    cancelExpressMutation.mutate({
                      expressId: patientToCancel.id,
                      reason: cancelReason,
                    })
                  }
                  disabled={cancelExpressMutation.isPending}
                >
                  {cancelExpressMutation.isPending ? 'Cancelando...' : 'Confirmar Cancelamento'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
